import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  CategoryScale,
  ArcElement,
} from "chart.js";
import { Line, Pie } from "react-chartjs-2";
import { startOfISOWeek, addDays, getISOWeek } from "date-fns";
import "chartjs-adapter-date-fns";
import zoomPlugin from "chartjs-plugin-zoom";
import { sv } from "date-fns/locale";
import { buildDateMap } from "./ChartUtils";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  CategoryScale,
  ArcElement,
  zoomPlugin
);

interface BookingsChartProps {
  rows: any[];
  type: "pie" | "line";
  startDate: string;
  endDate: string;
}

export function MembertypeChart({
  rows = [],
  type,
  startDate,
  endDate,
}: BookingsChartProps) {
  const [chartMax, setChartMax] = useState<number | null>(null);

  // --- 0️⃣ Toggle for week/date view ---
  const [groupByWeek, setGroupByWeek] = useState(false);

  // --- 1️⃣ Identify summary row(s) ---
  const summaryRows = rows.filter((r) => r.type === "summary");

  // --- 2️⃣ Extract weekdays dynamically ---
  const weekdays = Array.from(
    new Set(
      summaryRows.flatMap((row) =>
        Object.keys(row).filter(
          (k) =>
            !["__typename", "memberType", "week", "total", "type"].includes(
              k
            ) &&
            typeof row[k] === "object" &&
            Object.values(row[k]).some((v) => v !== null)
        )
      )
    )
  );

  const dateMap = buildDateMap(startDate, endDate);

  const points: { date: Date; week: number; weekday: string }[] = [];

  summaryRows.forEach((row) => {
    const week = row.week;

    weekdays.forEach((day) => {
      const cell = row?.[day];
      if (!cell || !Object.values(cell).some((v) => v !== null)) return;

      const date = dateMap.get(`${week}-${day}`);

      if (!date) return;

      points.push({
        date: date,
        week,
        weekday: day,
      });
    });
  });

  points.sort((a, b) => a.date.getTime() - b.date.getTime());

  // --- 6️⃣ Aggregate weekly or daily ---
  let displayPoints: { date: Date; week: number; weekday: string }[] = [];
  let capacityData: (number | null)[] = [];

  if (groupByWeek) {
    // --- Weekly aggregation ---
    const weekGroups = Array.from(new Set(points.map((p) => p.week))).map(
      (week) => {
        // Find all rows for this week
        const rowsForWeek = summaryRows.filter((r) => r.week === week);

        // Sum systemCapacity for all days in that week
        const totalCapacity = rowsForWeek.reduce((acc, row) => {
          return (
            acc +
            weekdays.reduce((sum, day) => {
              const c = row?.[day]?.systemCapacity ?? 0;
              return sum + (typeof c === "number" ? c : 0);
            }, 0)
          );
        }, 0);

        const firstPointForWeek = points.find((p) => p.week === week);

        return {
          week,
          date: firstPointForWeek?.date ?? new Date(),
          capacity: totalCapacity,
        };
      }
    );

    displayPoints = weekGroups.map((g) => ({
      date: g.date,
      week: g.week,
      weekday: "monday",
    }));
    capacityData = weekGroups.map((g) => g.capacity);
  } else {
    // --- Daily mode ---
    displayPoints = points;
    capacityData = points.map(({ week, weekday }) => {
      const row = summaryRows.find((r) => r.week === week);
      const cell = row?.[weekday];
      return cell?.systemCapacity ?? null;
    });
  }

  // --- 7️⃣ Labels ---
  const xLabels = groupByWeek
    ? displayPoints.map((p) => `Vecka ${p.week}`)
    : displayPoints.map((p) => p.date);

  // --- 8️⃣ Group rows by member type ---
  const grouped: Record<string, any[]> = rows
    .filter((r) => r.type === "row" && r.memberType !== "Totalt")
    .reduce((acc, row) => {
      const name = row.memberType?.trim() ?? "Okänd";
      if (!acc[name]) acc[name] = [];
      acc[name].push(row);
      return acc;
    }, {});

  const colors = [
    "#3F9C30", // grass green
    "#309C49", // jade green
    "#309C6B", // teal green
    "#309C8C", // sea teal
    "#30849C", // cerulean blue
    "#30629C", // mid blue
    "#303F9C", // indigo blue
    "#49309C", // violet
    "#6B309C", // deep purple
    "#8C309C", // magenta violet
    "#2D6E22", // darker grass green
    "#24753B", // darker jade green
    "#247559", // darker teal green
    "#24757A", // darker sea teal
    "#246C84", // darker cerulean blue
    "#214E84", // darker mid blue
    "#232E84", // darker indigo blue
    "#3A237D", // darker violet
    "#52237D", // darker deep purple
    "#70237D", // darker magenta violet
  ];

  // --- 9️⃣ Build aligned datasets per member type ---
  const memberDatasets = Object.entries(grouped).map(
    ([memberType, memberRows], idx) => {
      const data = groupByWeek
        ? displayPoints.map(({ week }) => {
            // sum all booker values for this week
            const rowsForWeek = memberRows.filter((r) => r.week === week);
            return rowsForWeek.reduce((sum, r) => {
              return (
                sum +
                weekdays.reduce((inner, day) => {
                  const c = r?.[day]?.booker ?? 0;
                  return inner + (typeof c === "number" ? c : 0);
                }, 0)
              );
            }, 0);
          })
        : displayPoints.map(({ week, weekday }) => {
            const weekRow = memberRows.find((r) => r.week === week);
            const cell = weekRow?.[weekday];
            return cell?.booker ?? 0;
          });

      const extra = displayPoints.map(({ week, weekday }) => {
        const weekRow = memberRows.find((r) => r.week === week);
        const cell = weekRow?.[weekday];
        return {
          participant: cell?.participant ?? 0,
          activity: cell?.activity ?? 0,
        };
      });

      return {
        label: memberType,
        data,
        customData: extra,
        borderWidth: 2,
        tension: 0.1,
        borderColor: colors[idx % colors.length],
        backgroundColor: colors[idx % colors.length] + "55",
        spanGaps: false,
      };
    }
  );

  const allMemberData = groupByWeek
    ? displayPoints.map(({ week }) => {
        // sum all member types' values for this week
        return Object.values(grouped).reduce((sum, memberRows: any[]) => {
          const rowsForWeek = memberRows.filter((r) => r.week === week);
          const weekSum = rowsForWeek.reduce((innerSum, r) => {
            return (
              innerSum +
              weekdays.reduce((inner, day) => {
                const c = r?.[day]?.booker ?? 0;
                return inner + (typeof c === "number" ? c : 0);
              }, 0)
            );
          }, 0);
          return sum + weekSum;
        }, 0);
      })
    : displayPoints.map(({ week, weekday }) => {
        return Object.values(grouped).reduce((sum, memberRows: any[]) => {
          const weekRow = memberRows.find((r) => r.week === week);
          const c = weekRow?.[weekday]?.booker ?? 0;
          return sum + (typeof c === "number" ? c : 0);
        }, 0);
      });

  const allMembersDataset = {
    label: "Alla valda spelrätter",
    data: allMemberData,
    borderWidth: 2,
    tension: 0.1,
    borderColor: "#30389c",
    backgroundColor: "#30389c55",
    borderDash: [4, 2],
    spanGaps: false,
  };

  // --- 🔟 Combine datasets ---
  const capacityDataset = {
    label: "Kapacitet",
    data: capacityData,
    borderWidth: 1,
    borderColor: "#888",
    borderDash: [6, 4],
    pointRadius: 3,
    pointBackgroundColor: "transparent",
    tension: 0.1,
    spanGaps: false,
  };

  const datasets = [capacityDataset, allMembersDataset, ...memberDatasets];

  // --- 🧭 Axis setup ---
  const numericCapacities = capacityData.filter((v): v is number => v != null);
  const maxCapacity = numericCapacities.length
    ? Math.max(...numericCapacities)
    : 10;

  const dailyValues = datasets
    .flatMap((d) => d.data)
    .filter((v): v is number => v != null && !isNaN(v));
  const dailyMax = Math.max(...dailyValues, 0);

  // max for weekly view (using capacityData + member rows)
  const weeklyValues = Object.values(grouped)
    .flatMap((memberRows: any[]) =>
      memberRows.map((r) =>
        weekdays.reduce((sum, day) => {
          const c = r?.[day]?.booker ?? 0;
          return sum + (typeof c === "number" ? c : 0);
        }, 0)
      )
    )
    .filter((v) => !isNaN(v));

  const weeklyMax = Math.max(
    ...capacityData.filter((v): v is number => v != null),
    0
  );

  // pick depending on current mode
  const activeMaxValue = groupByWeek ? weeklyMax : dailyMax;

  // Set default max when data first becomes available
  useEffect(() => {
    if (!activeMaxValue) return;
    setChartMax(Math.ceil(activeMaxValue * 1.1)); // 10% headroom
  }, [activeMaxValue, groupByWeek]);
  // Define dropdown options based on data
  const rangeOptions = [0.25, 0.5, 0.75, 1.1, 1.5].map((factor) => ({
    label: `${Math.round(activeMaxValue * factor)}`,
    value: Math.round(activeMaxValue * factor),
  }));

  if (!rows || rows.length === 0) return <div>Diagram</div>;

  // --- 🧭 UI Toggle + Chart ---
  if (type === "pie") {
    // --- 🥧 Find the total_summary row ---
    const totalSummaryRow = rows.find((r) => r.type === "total_summary");

    if (!totalSummaryRow) {
      return <div>Ingen totalrad hittades</div>;
    }

    // --- Extract total booked slots and capacity from total_summary ---
    // We use systemCapacity from summary rows for capacity (bookable slots)
    const totalCapacity = totalSummaryRow.total.systemBookings;

    // --- Aggregate total bookings per member type (based on booker) ---
    const groupedTotals = Object.entries(grouped).map(
      ([memberType, memberRows]) => {
        const totalBookingsMember = memberRows.reduce((sum, r) => {
          return (
            sum +
            weekdays.reduce((inner, day) => {
              const val = r?.[day]?.booker ?? 0;
              return inner + (typeof val === "number" ? val : 0);
            }, 0)
          );
        }, 0);

        return { memberType, totalBookings: totalBookingsMember };
      }
    );

    // --- Compute total booked count and unbooked capacity ---
    const totalBookings = groupedTotals.reduce(
      (acc, g) => acc + g.totalBookings,
      0
    );
    const remaining = Math.max(totalCapacity - totalBookings, 0);

    // --- Prepare data for Chart.js pie ---
    const labels = [
      ...groupedTotals.map((g) => g.memberType),
      ...(remaining > 0 ? ["Övriga spelrätter"] : []),
    ];

    const data = [
      ...groupedTotals.map((g) => g.totalBookings),
      ...(remaining > 0 ? [remaining] : []),
    ];

    return (
      <div className="pie-chart-container">
        <Pie
          data={{
            labels,
            datasets: [
              {
                data,
                backgroundColor: labels.map((label) =>
                  label === "Övriga spelrätter"
                    ? "#e0e0e0"
                    : memberDatasets.find((d) => d.label === label)
                        ?.borderColor || "#888888"
                ),
                borderColor: "#fff",
                borderWidth: 2,
              },
            ],
          }}
          options={{
            plugins: {
              tooltip: {
                callbacks: {
                  label: function (context: any) {
                    const label = context.label || "";
                    const value = context.parsed || 0;
                    const total = data.reduce((a, b) => a + b, 0);
                    const pct =
                      total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                    return `${label}: ${value} (${pct}%)`;
                  },
                },
              },
              legend: {
                position: "top" as const,
                labels: {
                  usePointStyle: true,
                  pointStyle: "circle",
                  boxWidth: 8,
                  boxHeight: 8,
                  padding: 12,
                },
              },
              title: {
                display: true,
                text: `Totala bokningar (bokare): ${Math.round(
                  totalBookings
                )} / Kapacitet: ${Math.round(totalCapacity)}`,
                font: { size: 14 },
              },
            },
          }}
        />
      </div>
    );
  }

  // --- UI Toggle + Chart (LINE) ---
  return (
    <div>
      <div className="chart-controls">
        <div className="chart-view-toggle">
          Visa per vecka
          <label className="toggle text-m">
            <input
              type="checkbox"
              checked={groupByWeek}
              onChange={(e) => setGroupByWeek(e.target.checked)}
            />
            <span className="slider round"></span>
          </label>
        </div>
        <div className="chart-max-select">
          <label>Maxvärde:</label>
          <select
            className="chart-max-select-dropdown"
            value={chartMax ?? ""}
            onChange={(e) =>
              setChartMax(e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value={maxCapacity * 1.1}>Auto</option>
            {rangeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Line
        data={{
          labels: xLabels as any,
          datasets,
        }}
        options={{
          scales: {
            x: {
              type: groupByWeek ? "category" : "time",
              offset: false,
              time: groupByWeek
                ? undefined
                : {
                    unit: "day",
                    tooltipFormat: "EEE dd/MM",
                    displayFormats: { day: "EEE dd/MM" },
                  },
              adapters: { date: { locale: sv } },
              title: {
                display: true,
                text: groupByWeek ? "Vecka" : "Datum",
              },
            },
            y: {
              beginAtZero: true,
              suggestedMin: -5, // let the chart extend a bit below 0
              suggestedMax: maxCapacity,
              min: -10, // hard lower limit (so negative lines are visible)
              max: Number(chartMax), // hard upper cap if you want to stop it
              title: {
                display: true,
                text: "Antal bokningar",
              },
              ticks: {
                stepSize: 5,
              },
            },
          },
          plugins: {
            legend: {
              position: "top" as const,
              labels: {
                usePointStyle: true,
                pointStyle: "circle",
                boxWidth: 8,
                boxHeight: 8,
                padding: 12,
              },
            },
            tooltip: {
              callbacks: {
                label: function (context: any) {
                  const { customData } = context.dataset as any;
                  const point = customData?.[context.dataIndex];
                  const label = context.dataset.label || "";
                  if (label === "Kapacitet") {
                    return `Kapacitet: ${context.parsed.y}`;
                  }

                  const base = `Bokare: ${context.parsed.y}`;
                  if (label === "Alla valda spelrätter") {
                    return [label, base];
                  }
                  if (!point) return base;
                  return [
                    label,
                    base,
                    `Medspelare: ${point.participant}`,
                    `Aktiviteter: ${point.activity}`,
                  ];
                },
              },
            },
          },
        }}
      />
    </div>
  );
}
